import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import { DEMO_SEED } from "./demoSeed.js";

// ---------------------------------------------------------------------------
// /demo mode — same app, isolated + seeded data.
// App.tsx persists to a single localStorage key (currently
// "wtw-cm-platform:demo:v3", but Craig bumps the version on new builds). When
// we're on the /demo path we transparently redirect ALL of the app's storage
// keys (anything under the "wtw-cm-platform:" prefix) to a parallel ":demo"
// bucket, so the demo's dummy data can never collide with a real consultant's
// project on prod. On first read we serve the seed below. All of this lives
// here, not in App.tsx, so it survives Craig's whole-file App.tsx drops.
// ---------------------------------------------------------------------------
const APP_KEY_PREFIX = "wtw-cm-platform:";
const isDemo = window.location.pathname.replace(/\/+$/, "").endsWith("/demo");

if (isDemo) {
  const _get = localStorage.getItem.bind(localStorage);
  const _set = localStorage.setItem.bind(localStorage);
  const _remove = localStorage.removeItem.bind(localStorage);
  const isAppKey = (k) => typeof k === "string" && k.startsWith(APP_KEY_PREFIX);
  const demoKey = (k) => `${k}:demo`;

  localStorage.getItem = (k) => {
    if (!isAppKey(k)) return _get(k);
    const existing = _get(demoKey(k));
    if (existing != null) return existing;
    // First load of the demo: serve (and persist) the seed if we have one.
    if (DEMO_SEED) {
      const seed = JSON.stringify(DEMO_SEED);
      _set(demoKey(k), seed);
      return seed;
    }
    return null;
  };
  localStorage.setItem = (k, v) => _set(isAppKey(k) ? demoKey(k) : k, v);
  localStorage.removeItem = (k) => _remove(isAppKey(k) ? demoKey(k) : k);
}

const root = document.getElementById("root");
if (!root) throw new Error("Missing #root element");

// /certnotes — a standalone SWC page (Lagree-cert comms drafts for Craig),
// unrelated to the CM tool. Rendered here instead of <App/> so it stays out of
// Craig's App.tsx and survives his whole-file drops. Lazy-loaded so it never
// ships in the main app's critical path.
const isCertNotes = window.location.pathname.replace(/\/+$/, "").endsWith("/certnotes");

if (isCertNotes) {
  import("./CertNotes.jsx").then(({ default: CertNotes }) => {
    createRoot(root).render(
      <StrictMode>
        <CertNotes />
      </StrictMode>,
    );
  });
} else {
  createRoot(root).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
}
